/**
 * Files API client — companion to Device Sync for attachments / large blobs.
 * Mirrors iOS DeviceSyncService / DeviceSyncAPI file upload/download flow.
 */

import { API_BASE_URL, buildAuthHeaders, getUserSubFromToken } from './apiClient';

/** Default retention for sync files — mirrors iOS DeviceSyncConstants.syncFileTTLYears */
const SYNC_FILE_TTL_YEARS = 3;

export interface FileUploadSession {
  fileId: string;
  uploadUrl: string;
}

function cleanupAtIso(yearsFromNow = SYNC_FILE_TTL_YEARS): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + yearsFromNow);
  return d.toISOString();
}

/**
 * POST /files — body matches OpenAPI UserFileBody (userId + cleanupAt required).
 * Response is PostFileResponseBody: { file: { id }, uploadUrl }.
 */
export async function createFileUpload(
  contentType: string,
  options: { filename?: string; byteSize?: number } = {}
): Promise<FileUploadSession> {
  const userId = getUserSubFromToken();
  if (!userId) {
    throw new Error('createFileUpload: not signed in');
  }

  const headers = await buildAuthHeaders(true);
  const body: Record<string, unknown> = {
    userId,
    cleanupAt: cleanupAtIso(),
    contentType,
  };
  if (options.filename) body.filename = options.filename;
  if (options.byteSize != null) {
    body.metadata = { byteSize: String(options.byteSize), sync: 'true' };
  } else {
    body.metadata = { sync: 'true' };
  }

  const response = await fetch(`${API_BASE_URL}/files`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`createFileUpload failed: ${response.status}`);
  }

  const json = (await response.json()) as {
    fileId?: string;
    uploadUrl?: string;
    id?: string;
    file?: { id?: string; fileId?: string };
  };
  const fileId = String(json.file?.id ?? json.file?.fileId ?? json.fileId ?? json.id ?? '');
  const uploadUrl = String(json.uploadUrl ?? '');
  if (!fileId || !uploadUrl) {
    throw new Error('createFileUpload: missing fileId or uploadUrl');
  }
  return { fileId, uploadUrl };
}

export async function confirmFileUpload(fileId: string): Promise<void> {
  const headers = await buildAuthHeaders(true);
  const response = await fetch(
    `${API_BASE_URL}/files/${encodeURIComponent(fileId)}/confirm`,
    { method: 'POST', headers, body: '{}' }
  );
  if (!response.ok) {
    throw new Error(`confirmFileUpload failed: ${response.status}`);
  }
}

/**
 * GET /files/{id}/download returns { downloadUrl }; fetch that URL for bytes.
 */
export async function downloadFile(fileId: string): Promise<Blob> {
  const headers = await buildAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/files/${encodeURIComponent(fileId)}/download`,
    { method: 'GET', headers }
  );
  if (!response.ok) {
    throw new Error(`downloadFile failed: ${response.status}`);
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    const json = (await response.json()) as { downloadUrl?: string; url?: string };
    const downloadUrl = String(json.downloadUrl ?? json.url ?? '');
    if (!downloadUrl) {
      throw new Error('downloadFile: missing downloadUrl');
    }
    const fileResponse = await fetch(downloadUrl);
    if (!fileResponse.ok) {
      throw new Error(`downloadFile fetch failed: ${fileResponse.status}`);
    }
    return fileResponse.blob();
  }

  return response.blob();
}

/**
 * Upload bytes and return a fileId suitable for attachmentRefs / largeCityBlobs.
 */
export async function uploadDeviceSyncFile(
  data: Blob | ArrayBuffer,
  contentType: string,
  filename?: string
): Promise<string> {
  const byteSize =
    data instanceof Blob ? data.size : data.byteLength;
  const { fileId, uploadUrl } = await createFileUpload(contentType, {
    filename,
    byteSize,
  });
  const put = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: data instanceof Blob ? data : new Blob([data], { type: contentType }),
  });
  if (!put.ok) {
    throw new Error(`uploadDeviceSyncFile PUT failed: ${put.status}`);
  }
  await confirmFileUpload(fileId);
  return fileId;
}
